import { useState, useCallback, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Settings } from "@mui/icons-material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { ShareAvatar } from "./share-avatar";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader";
import * as THREE from "three";
import { ref, getDownloadURL } from "firebase/storage";
import { useCustomNavigation } from "@/hooks/useCustomNavigation";

import { useScanData } from "@/hooks/useScanData";
import { useIsMobile } from "@/hooks/useIsMobile";
import { Spinner } from "@/Components/Spinner";
import { storage } from "@/Utils/firebase";
import { SisleyInfoPanel } from "./SisleyInfoPanel";

import "./avatarPreview.scss";

const isSisleyEnv = process.env.REACT_APP_IS_SISLEY_ENV === "true";

// const SKY_COLOR = 0xdef3ff;
// const GROUND_COLOR = 0xe8b65e;
// const SKY_SIZE = 1000000;
// const FOV = 66;

function addSkyGradient(scene) {
  const geometry = new THREE.SphereGeometry(10, 10, 30);
  const material = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    side: THREE.DoubleSide,
  });
  const sphere = new THREE.Mesh(geometry, material);

  sphere.position.set(0, 0, 0);
  scene.add(sphere);
}

const AvatarPreview = () => {
  const location = useLocation();
  const { navigateTo, navigateBack } = useCustomNavigation();
  const [isOpen, setIsOpen] = useState(false);
  const [objLoading, setObjLoading] = useState(true);
  const { name = "", date = "", id } = location.state || {};
  const scanData = useScanData(id)?.data;
  const storagePath = scanData?.storagePath;
  const [objUrl, setObjUrl] = useState(null);
  const isMobile = useIsMobile();
  const headerRef = useRef(null);
  const footerRef = useRef(null);
  const scene = useRef();
  const camera = useRef();
  const renderer = useRef();
  const controls = useRef();
  const initCalled = useRef(false);

  const animate = useCallback(() => {
    renderer.current?.render(scene.current, camera.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderer.current]);

  const resetView = useCallback(() => {
    if (!controls.current) return;
    controls.current.reset();
  }, []);

  const updateCanvasDimensions = useCallback(
    (width, height) => {
      // update the container dom element
      const container = document.getElementById("viewer-canvas");
      if (container) {
        container.style.width = `${width}px`;
        container.style.height = `${height}px`;
      }
      if (!renderer.current || !camera.current) return;
      renderer.current.setSize(width, height);
      camera.current.aspect = width / height;
      // camera.current.updateProjectionMatrix();
      resetView();
    },
    [resetView]
  );

  const updateCameraOnResize = useCallback((width, height) => {
    // `camera.current` only exists once `init()` has run, which waits on the
    // scan doc → storagePath → obj download URL. A resize before that (rotating
    // the phone while the avatar is still loading, or a scan whose doc never
    // resolves) used to throw "Cannot set properties of undefined (setting
    // 'left')" straight out of the resize listener. `updateCanvasDimensions`
    // already guards the same way.
    if (!camera.current) return;
    const frustumSize = 2;
    const aspectRatio = width / height;
    camera.current.left = (frustumSize * aspectRatio) / -2;
    camera.current.right = (frustumSize * aspectRatio) / 2;
    camera.current.top = frustumSize / 2;
    camera.current.bottom = frustumSize / -2;
    camera.current.updateProjectionMatrix();
  }, []);

  const handleWindowResize = () => {
    const width = window.innerWidth;
    const headerHeight = headerRef.current?.clientHeight || 0;
    const footerHeight = footerRef.current?.clientHeight || 0;
    const height = window.innerHeight - headerHeight - footerHeight;
    updateCameraOnResize(width, height);
    updateCanvasDimensions(width, height);
  };

  useEffect(() => {
    window.addEventListener("resize", handleWindowResize);
    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const init = useCallback(
    (url) => {
      const width = window.innerWidth;
      const headerHeight = headerRef.current?.clientHeight || 0;
      const footerHeight = footerRef.current?.clientHeight || 0;
      const height = window.innerHeight - headerHeight - footerHeight;
      const sceneAlreadyExists = Boolean(scene.current);
      const cameraAlreadyExists = Boolean(camera.current);
      const rendererAlreadyExists = Boolean(renderer.current);
      const controlsAlreadyExists = Boolean(controls.current);
      if (!sceneAlreadyExists) {
        scene.current = new THREE.Scene();
        scene.current.fog = new THREE.Fog(0xf0f2fa, 1, 3);
        scene.current.background = new THREE.Color(0xf0f2fa);
        scene.current.add(new THREE.AmbientLight(0xffffff, 1.2));

        /////////////////////////////////////////////////////////////////////////////
        const sunlight = new THREE.DirectionalLight(0xffffff, 0.5);
        sunlight.position.set(0, 100, 0);
        scene.current.add(sunlight);

        // lights front
        const light = new THREE.DirectionalLight(0xffffff, 2.5);
        light.position.set(0, -12, 30);
        scene.current.add(light);

        const hemiLight = new THREE.HemisphereLight(0xffffff, 0x8d8d8d, 0.4);
        hemiLight.position.set(0, 50, 0);
        scene.current.add(hemiLight);

        // back
        const light2 = new THREE.DirectionalLight(0xffffff, 2); // 0xebf8ff
        light2.position.set(0, 0, -30);
        light2.target.position.set(0, 0, 0);
        light2.shadow.radius = 10;
        scene.current.add(light2);

        const spotLight = new THREE.SpotLight(0xffffff, 2);
        spotLight.position.set(0, 8, 0);
        spotLight.castShadow = true;
        spotLight.shadow.radius = 10;
        spotLight.angle = Math.PI / 3;
        spotLight.penumbra = 0.2;
        spotLight.distance = 15;
        spotLight.shadow.mapSize.width = 2048;
        spotLight.shadow.mapSize.height = 2048;
        spotLight.shadow.camera.near = 0.5;
        spotLight.shadow.camera.far = 5;
        spotLight.shadow.camera.fov = 30;
        scene.current.add(spotLight);

        addSkyGradient(scene.current);
        ////////////////////////////////////////////////////////////

        // Model
        const loader = new OBJLoader();
        loader.load(
          url,
          (object) => {
            object.children[0].geometry.computeBoundingBox();
            const center = object.children[0].geometry.boundingBox.getCenter(
              new THREE.Vector3()
            );
            controls.current.object.position.set(0, center.y, 1.5);
            controls.current.target = center;
            controls.current.saveState();
            animate();
            object.children[0].material = new THREE.MeshPhongMaterial({
              color: 0xd6d9e6,
              shininess: 0,
            });
            object.children[0].castShadow = false;
            object.children[0].receiveShadow = false;
            object.renderOrder = 0;
            scene.current.add(object);
            window.requestAnimationFrame(() => {
              animate();
            });
            controls.current.update();
            setObjLoading(false);
          },
          undefined,
          (error) => {
            console.log(`Error loading obj: ${error}`);
          }
        );
      }
      //////////////////////////////////////////////
      // if (!cameraAlreadyExists) {
      //   camera.current = new THREE.PerspectiveCamera(
      //     FOV, // fov = field of view
      //     window.innerWidth / height, // aspect ratio
      //     0.1, // near plane
      //     1000 // far plane
      //   );
      // }
      // camera.current.aspect = width / height;
      // camera.current.fov = FOV;
      // camera.current.updateProjectionMatrix();
      // camera.current.position.z = 1.5; // is used here to set some distance from the model located at z = 0
      ////////////////////////////////////////////////

      const frustumSize = 2;
      let aspectRatio =
        window.innerWidth /
        (window.innerHeight -
          (headerRef.current?.clientHeight || 0) -
          (footerRef.current?.clientHeight || 0));

      if (!cameraAlreadyExists) {
        camera.current = new THREE.OrthographicCamera(
          (frustumSize * aspectRatio) / -2, // left
          (frustumSize * aspectRatio) / 2, // right
          frustumSize / 2, // top
          frustumSize / -2, // bottom
          0.1, // near plane
          15 // far plane
        );
      }

      // function updateCameraOnResize() {
      //   aspectRatio = window.innerWidth / window.innerHeight;
      //   camera.current.left = (frustumSize * aspectRatio) / -2;
      //   camera.current.right = (frustumSize * aspectRatio) / 2;
      //   camera.current.top = frustumSize / 2;
      //   camera.current.bottom = frustumSize / -2;
      //   camera.current.updateProjectionMatrix();
      // }

      camera.current.position.z = 1.5;

      ///////////////////////////////////////////////
      if (!rendererAlreadyExists) {
        renderer.current = new THREE.WebGLRenderer({ antialias: true });
        // renderer.current.setClearColor(scene.current.fog.color);
        renderer.current.shadowMap.enabled = false;
        renderer.current.shadowMap.type = THREE.PCFSoftShadowMap;
      }
      renderer.current.setSize(width, height);
      if (!controlsAlreadyExists) {
        // OrbitControls allow a camera to orbit around the object
        // https://threejs.org/docs/#examples/controls/OrbitControls
        controls.current = new OrbitControls(
          camera.current,
          renderer.current.domElement
        );

        // Set min and max polar angle to restrict the orbit to the y-axis
        controls.current.minPolarAngle = Math.PI / 2; // Set to 90 degrees
        controls.current.maxPolarAngle = Math.PI / 2; // Set to 90 degrees

        // Set azimuth angle limits (optional for specific y-axis control)
        controls.current.minAzimuthAngle = -Infinity; // No limit on rotation around y-axis
        controls.current.maxAzimuthAngle = Infinity; // No limit on rotation around y-axis

        controls.current.addEventListener("change", () => {
          animate();
        });
      }

      const container = document.getElementById("viewer-canvas");
      if (container) {
        container.style.width = `${width}px`;
        container.style.height = `${height}px`;
      }
      if (!container?.querySelector("canvas")) {
        container?.appendChild(renderer.current.domElement);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isMobile]
  );

  useEffect(() => {
    if (!storagePath) {
      return;
    }
    const getObjUrl = async () => {
      const objectUrl = `${storagePath}/avatar/full_model_mesh.obj`;
      const storageRef = ref(storage, objectUrl);
      const url = await getDownloadURL(storageRef);
      setObjUrl(url);
    };
    getObjUrl();
    // Cleanup function
    return () => {
      if (controls.current) {
        controls.current.dispose();
      }
      if (renderer.current) {
        document
          .querySelector("#viewer-canvas")
          ?.removeChild(renderer.current.domElement);
        renderer.current?.dispose();
      }
    };
  }, [storagePath]);

  useEffect(() => {
    if (objUrl && !initCalled.current) {
      init(objUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objUrl]);

  return (
    <>
      <div
        style={{
          background: "var(--sm-bg, rgba(240, 242, 250, 1))",
        }}
        className="main-grid"
      >
        <div className="avatar-header-wrap" ref={headerRef}>
          <div>
            <h2>
              {" "}
              <ChevronLeftIcon
                onClick={() => navigateTo("/")}
                className="back-icon"
              />
              Avatar Preview
              <Settings
                onClick={() => {
                  navigateTo("/settings");
                }}
                className="settings-icon"
              />
            </h2>
          </div>
          <div>
            {name} • {date}
          </div>
          {/* Sisley: the doc puts the email right under the name • date line so
              the customer can double-check it. Only rendered when the scan doc
              actually carries one. */}
          {isSisleyEnv && scanData?.email && (
            <div className="avatar-header-email">{scanData.email}</div>
          )}
        </div>
        <div className="canvas-container" id="viewer-canvas">
          {objLoading && (
            <div className="spinner-container">
              <Spinner />
            </div>
          )}
          {isSisleyEnv && <SisleyInfoPanel scan={scanData} />}
        </div>
        <div className="avatar-footer-container" ref={footerRef}>
          <button
            className="new-avatar-button"
            onClick={() => {
              setIsOpen(true);
            }}
          >
            Share
          </button>
        </div>
      </div>
      <ShareAvatar
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
      />
    </>
  );
};

export default AvatarPreview;
